import { computed, nextTick, ref, type ComputedRef, type Ref } from "vue";
import { useRouter } from "vue-router";
import { trackBehaviorEvent } from "~/core/utils/behaviorAnalytics";

// Enough time for the menu to close without making navigation feel delayed.
// Route transitions preserve the old visual state while the destination loads.
export const MENU_NAVIGATION_DELAY = 320;

export function useHeaderMenuState(deps: {
  isExternalOverlayActive: Ref<boolean>;
  isSearchInFocus: Ref<Boolean>;
  desktopSearchRef: Ref<HTMLElement | null>;
  desktopMenuItems: ComputedRef<any[]>;
}) {
  const {
    isExternalOverlayActive,
    isSearchInFocus,
    desktopSearchRef,
    desktopMenuItems,
  } = deps;

  const router = useRouter();

  const menuRef = ref<HTMLElement | null>(null);
  const openStates = ref([false, false, false, false]);
  const isMobileMenuOpen = computed(() => openStates.value[3]);
  const isMenuHover = ref(false);
  const isMenuButtonsHover = ref(false);

  // Add timeout ref for category hover delay
  const categoryHoverTimeout = ref<NodeJS.Timeout | null>(null);

  let delayedMenuNavigationTimer: number | null = null;

  const activeDesktopMenuItem = computed(() => {
    const activeIndex = openStates.value.slice(0, 3).findIndex(Boolean);
    return activeIndex >= 0 ? desktopMenuItems.value[activeIndex] : null;
  });

  const openPanel = (index: number) => {
    if (isExternalOverlayActive.value) {
      return;
    }

    const wasOpen = Boolean(openStates.value[index]);
    openStates.value = openStates.value.map((_, i) => i === index);
    if (!wasOpen && index < 3) trackBehaviorEvent("menu_open_desktop");
  };

  // Clicking a menu button switches the panel immediately (no hover delay)
  const switchMenuPanel = (index: number) => {
    if (categoryHoverTimeout.value) {
      clearTimeout(categoryHoverTimeout.value);
      categoryHoverTimeout.value = null;
    }
    openPanel(index);
  };

  const closePanel = () => {
    setTimeout(() => {
      if (!isMenuHover.value && !isMenuButtonsHover.value) {
        openStates.value = openStates.value?.map((x) => false);
      }
    }, 100);
  };

  const togglePanel = (panelIndex: number) => {
    if (isExternalOverlayActive.value) {
      return;
    }

    const willOpen = !openStates.value[panelIndex];
    openStates.value = openStates.value.map((state, index) =>
      index === panelIndex ? !state : false
    );
    if (willOpen && panelIndex === 3) {
      trackBehaviorEvent("menu_open_mobile");
    }
  };

  const closeMobileMenu = () => {
    openStates.value = openStates.value.map(() => false);
  };

  const closeAllMenusForNavigation = () => {
    isMenuHover.value = false;
    isMenuButtonsHover.value = false;
    isSearchInFocus.value = false;
    openStates.value = openStates.value.map(() => false);
  };

  const navigateAfterMenuAnimation = (to: any) => {
    if (!to) return;

    if (delayedMenuNavigationTimer !== null && typeof window !== "undefined") {
      window.clearTimeout(delayedMenuNavigationTimer);
    }

    const navigate = () => {
      delayedMenuNavigationTimer = null;
      if (typeof window !== "undefined") {
        // Catalog views intentionally retain their current data until the next
        // GraphQL request resolves. A client-side menu transition therefore
        // leaves the previous category visible for several seconds and makes
        // a correct destination look broken. Header links use a fresh SSR
        // document so the first committed catalog is always the selected one.
        window.location.assign(router.resolve(to).href);
        return;
      }
      router.push(to);
    };

    if (typeof window === "undefined") {
      navigate();
      return;
    }

    delayedMenuNavigationTimer = window.setTimeout(
      navigate,
      MENU_NAVIGATION_DELAY
    );
  };

  const handleMobileMenuLinkNavigation = (to: any) => {
    trackBehaviorEvent("menu_navigate_mobile");
    closeMobileMenu();
    navigateAfterMenuAnimation(to);
  };

  const handleDesktopMenuLinkNavigation = (to: any) => {
    trackBehaviorEvent("menu_navigate_desktop");
    closeAllMenusForNavigation();
    navigateAfterMenuAnimation(to);
  };

  const handleGlobalEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      if (openStates.value.some((state) => state)) {
        e.preventDefault();
        openStates.value = openStates.value.map(() => false);
      }
      if (isSearchInFocus.value) {
        isSearchInFocus.value = false;
      }
    }
  };

  const handleMenuKeydown = (e: KeyboardEvent, index: number) => {
    const maxIndex = desktopMenuItems.value.length - 1;
    switch (e.key) {
      case "ArrowRight": {
        e.preventDefault();
        const nextIndex = Math.min(index + 1, maxIndex);
        closePanel();
        nextTick(() => {
          const buttons = menuRef.value?.querySelectorAll(
            ".vf-m-flyout-menu button"
          ) as NodeListOf<HTMLElement>;
          buttons?.[nextIndex]?.focus();
        });
        break;
      }
      case "ArrowLeft": {
        e.preventDefault();
        const prevIndex = Math.max(index - 1, 0);
        closePanel();
        nextTick(() => {
          const buttons = menuRef.value?.querySelectorAll(
            ".vf-m-flyout-menu button"
          ) as NodeListOf<HTMLElement>;
          buttons?.[prevIndex]?.focus();
        });
        break;
      }
      case "ArrowDown":
      case "Enter":
      case " ": {
        e.preventDefault();
        openPanel(index);
        nextTick(() => {
          const panel = document.querySelector(".new-header-menu-panel .web");
          const firstLink = panel?.querySelector(
            'a, button, [tabindex="0"]'
          ) as HTMLElement | null;
          firstLink?.focus();
        });
        break;
      }
      case "Escape": {
        e.preventDefault();
        closePanel();
        const buttons = menuRef.value?.querySelectorAll(
          ".vf-m-flyout-menu button"
        ) as NodeListOf<HTMLElement>;
        buttons?.[index]?.focus();
        break;
      }
    }
  };

  const headerMenuButtonsHover = (index: number) => {
    if (!isSearchInFocus.value) {
      isMenuButtonsHover.value = true;

      // Clear any existing timeout
      if (categoryHoverTimeout.value) {
        clearTimeout(categoryHoverTimeout.value);
        categoryHoverTimeout.value = null;
      }

      // Check if any menu is currently open
      const isAnyMenuOpen = openStates.value.slice(0, 3).some((state) => state);

      if (isAnyMenuOpen) {
        // If a menu is open, add delay before switching to another menu
        categoryHoverTimeout.value = setTimeout(() => {
          openPanel(index);
          categoryHoverTimeout.value = null;
        }, 500);
      } else {
        // If no menu is open, open immediately
        openPanel(index);
      }
    }
  };

  const headerMenuButtonsLeave = () => {
    isMenuButtonsHover.value = false;

    // Clear timeout if user leaves before delay completes
    if (categoryHoverTimeout.value) {
      clearTimeout(categoryHoverTimeout.value);
      categoryHoverTimeout.value = null;
    }

    closePanel();
  };

  const menuMouseOver = () => {
    isMenuHover.value = true;
  };
  const menuMouseOut = () => {
    isMenuHover.value = false;
    closePanel();
  };

  const handleClickOutside = (event: any) => {
    const mobileToggle = document.querySelector(
      ".vf-o-new-header-mobile__toggle"
    );

    if (
      desktopSearchRef.value &&
      desktopSearchRef.value.contains(event.target)
    ) {
      return;
    }

    if (mobileToggle && mobileToggle.contains(event.target as Node)) {
      return; // Exit the function if mobile toggle is clicked
    }
    if (menuRef.value && !menuRef.value.contains(event.target as Node)) {
      if (!(event?.target as Element)?.closest(".new-header-menu-panel")) {
        closePanel();
      }
    }
  };

  const disposeMenuState = () => {
    // Clear timeout on component unmount
    if (categoryHoverTimeout.value) {
      clearTimeout(categoryHoverTimeout.value);
    }
    if (delayedMenuNavigationTimer !== null) {
      window.clearTimeout(delayedMenuNavigationTimer);
      delayedMenuNavigationTimer = null;
    }
  };

  return {
    menuRef,
    openStates,
    isMobileMenuOpen,
    isMenuHover,
    isMenuButtonsHover,
    activeDesktopMenuItem,
    openPanel,
    switchMenuPanel,
    closePanel,
    togglePanel,
    closeMobileMenu,
    closeAllMenusForNavigation,
    navigateAfterMenuAnimation,
    handleMobileMenuLinkNavigation,
    handleDesktopMenuLinkNavigation,
    handleGlobalEscape,
    handleMenuKeydown,
    headerMenuButtonsHover,
    headerMenuButtonsLeave,
    menuMouseOver,
    menuMouseOut,
    handleClickOutside,
    disposeMenuState,
  };
}
